import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, type Ref, useEffect, useImperativeHandle, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Count } from './Count';
import { Separator } from './Separator';
import { Heading, Heading3, LabelPlus, Paragraph, Subheading } from './Text';

const AccordionElement = styled(Box)<{ borderless?: boolean }>`
	${(props) =>
		!props.borderless &&
		`
		border: 1px solid ${tokens.border.subtle};
		background-color: ${tokens.surface.white};
		border-radius: ${tokens.radius.sm}px;
		:not(:last-child) {
			margin-bottom: ${tokens.gap.list}px;
		}
	`}

	${(props) =>
		props.borderless &&
		`
		:not(:last-child) {
			border-bottom: 1px solid ${tokens.border.subtle};
		}
	`}
`;

const AccordionChevron = styled(Box)<{ $size: number; $muted?: boolean }>`
	width: ${(props) => props.$size}px;
	height: ${(props) => props.$size}px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${(props) => (props.$muted ? tokens.icon.tertiary : tokens.icon.primary)};

	svg {
		width: ${(props) => props.$size}px;
		height: ${(props) => props.$size}px;
		display: block;
	}
`;

const AccordionHeader = styled.button<{ isCollapsed: boolean; hasCheckbox: boolean }>`
	padding: 0;
	background-color: transparent;
	width: 100%;
	text-align: start;
	overflow: visible;

	${AccordionChevron} {
		transition: transform 120ms ease-out;
		transform: ${(props) => (!props.isCollapsed && !props.hasCheckbox ? 'rotate(90deg)' : '')};
	}
`;

const HeaderRow = styled.div<{ $compact?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.gap.list}px;
`;

const HeaderMain = styled(Flex)`
	align-items: center;
	justify-content: flex-start;
	min-width: 0;
	flex: 1 1 auto;
`;

const TitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.element}px;
	min-width: 0;
`;

const HeaderRight = styled.div<{ $compact?: boolean }>`
	flex-shrink: 0;
	display: flex;
	align-items: center;

	${(props) =>
		props.$compact &&
		`
		height: 0;
		overflow: visible;
	`}
`;

type Handle = {
	open: () => void;
	close: () => void;
};

type AccordionProps = {
	title?: string;
	count?: number;
	small?: boolean;
	large?: boolean;
	compact?: boolean;
	description?: string | ReactNode;
	children: ReactNode;
	initialIsCollapsed?: boolean;
	onClick?: () => void;
	isExpanded?: boolean;
	index?: number;
	checkbox?: ReactNode;
	noPadding?: boolean;
	borderless?: boolean;
	headerRight?: ReactNode;
	footer?: ReactNode;
	customHeader?: ReactNode;
	handleRef?: Ref<Handle>;
};

function TitleWithCount({ title, count }: { title: ReactNode; count?: number }) {
	if (count == null) return <>{title}</>;

	return (
		<TitleRow>
			{title}
			<Count count={count} />
		</TitleRow>
	);
}

export const Accordion = ({
	title,
	count,
	small,
	large,
	compact,
	description,
	children,
	initialIsCollapsed = true,
	onClick,
	isExpanded,
	index,
	checkbox,
	borderless,
	noPadding,
	headerRight,
	customHeader,
	footer,
	handleRef,
}: AccordionProps) => {
	const [isCollapsed, setIsCollapsed] = useState<boolean>(initialIsCollapsed);

	useEffect(() => {
		if (isExpanded !== undefined) {
			setIsCollapsed(!isExpanded);
		}
	}, [isExpanded]);

	useImperativeHandle(handleRef, () => ({
		open: () => setIsCollapsed(false),
		close: () => setIsCollapsed(true),
	}));

	return (
		<AccordionElement borderless={borderless}>
			<AccordionHeader
				isCollapsed={isCollapsed}
				hasCheckbox={!!checkbox}
				onClick={() => {
					onClick?.();
					if (index === undefined) {
						setIsCollapsed(!isCollapsed);
					}
				}}
			>
				{customHeader ? (
					customHeader
				) : (
					<Box py={compact ? `${tokens.padding.container}px` : 2} px={compact ? 0 : 2}>
						<HeaderRow $compact={compact}>
							<HeaderMain>
								<AccordionChevron
									mr={1}
									$size={compact || small ? 16 : 22}
									$muted={small || compact}
								>
									{checkbox ?? <ChevronRightIcon size={compact || small ? 16 : 22} />}
								</AccordionChevron>
								<Box minWidth={0} maxWidth="95%">
									{large ? (
										<TitleWithCount count={count} title={<Heading>{title}</Heading>} />
									) : compact ? (
										<TitleWithCount count={count} title={<LabelPlus>{title}</LabelPlus>} />
									) : (
										<>
											<TitleWithCount
												count={count}
												title={
													small ? <Heading3>{title}</Heading3> : <Subheading>{title}</Subheading>
												}
											/>
											<Paragraph>{description}</Paragraph>
										</>
									)}
								</Box>
							</HeaderMain>
							{headerRight && <HeaderRight $compact={compact}>{headerRight}</HeaderRight>}
						</HeaderRow>
					</Box>
				)}
			</AccordionHeader>

			<AnimatePresence initial={false}>
				{!isCollapsed && (
					<motion.div
						key="accordion-content"
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: 'auto', opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.2, ease: 'easeOut' }}
						style={{ overflow: 'hidden' }}
					>
						<Box px={small ? 0 : 2} pb={borderless ? 3 : 2} {...(noPadding && { p: 0 })}>
							{children}

							{footer && (
								<>
									<Separator />
									<Box>{footer}</Box>
								</>
							)}
						</Box>
					</motion.div>
				)}
			</AnimatePresence>
		</AccordionElement>
	);
};

function AccordionSkeleton({ borderless }: { borderless?: boolean }) {
	return (
		<AccordionElement borderless={borderless}>
			<Box px={2} py={2}>
				<Flex alignItems="center">
					<AccordionChevron mr={1} $size={22} $muted>
						<ChevronRightIcon size={22} />
					</AccordionChevron>
					<Paragraph.Skeleton lines={1} mb={1} />
				</Flex>
			</Box>
		</AccordionElement>
	);
}

Accordion.Skeleton = AccordionSkeleton;
